import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  AccessAuditEntry,
  AccessAuditAction,
  AccessAuditLogListResponse,
  AccessAuditResourceKind,
  AccessBindingInput,
  AccessBindingResource,
  AccessBindingsListResponse,
  AccessReconcileJournalEntry,
  AccessReconcileCleanTablesAllowedResponse,
  AccessReconcileCleanTablesResponse,
  AccessReconcileJournalListResponse,
  AccessReconcileMaterialization,
  AccessReconcileMaterializationsListResponse,
  AccessReconcileRequest,
  AccessReconcileResponse,
  AccessReconcileResourceState,
  AccessReconcileResourceStatesListResponse,
  AccessReconcileRunDetails,
  AccessReconcileRunsListResponse,
  AccessRoleInput,
  AccessRoleResource,
  AccessRolesListResponse,
} from 'adp-access-common';

export type AccessApi = {
  getAuditLog(options?: {
    limit?: number;
    offset?: number;
    sortBy?: 'createdAt' | 'actor';
    order?: 'asc' | 'desc';
    resourceKind?: AccessAuditResourceKind;
    action?: AccessAuditAction;
    search?: string;
  }): Promise<{
    items: AccessAuditEntry[];
    total: number;
    limit: number;
    offset: number;
  }>;
  getRoles(): Promise<AccessRoleResource[]>;
  getRole(id: string): Promise<AccessRoleResource>;
  createRole(role: AccessRoleInput): Promise<AccessRoleResource>;
  updateRole(id: string, role: AccessRoleInput): Promise<AccessRoleResource>;
  deleteRole(id: string): Promise<void>;
  getBindings(): Promise<AccessBindingResource[]>;
  getBinding(id: string): Promise<AccessBindingResource>;
  createBinding(binding: AccessBindingInput): Promise<AccessBindingResource>;
  updateBinding(
    id: string,
    binding: AccessBindingInput,
  ): Promise<AccessBindingResource>;
  deleteBinding(id: string): Promise<void>;
  reconcile(request: AccessReconcileRequest): Promise<AccessReconcileResponse>;
  canCleanReconcileTables(): Promise<boolean>;
  cleanReconcileTables(): Promise<AccessReconcileCleanTablesResponse>;
  getReconcileRuns(options?: {
    limit?: number;
    offset?: number;
  }): Promise<AccessReconcileRunsListResponse>;
  getReconcileRunDetails(runId: string): Promise<AccessReconcileRunDetails>;
  getReconcileStates(options?: {
    limit?: number;
    phase?: string;
    runId?: string;
  }): Promise<AccessReconcileResourceState[]>;
  getReconcileMaterializations(options?: {
    limit?: number;
    ownerKind?: string;
    ownerRef?: string;
    taskId?: string;
    status?: string;
  }): Promise<AccessReconcileMaterialization[]>;
  getReconcileJournal(options?: {
    limit?: number;
    runId?: string;
    ownerRef?: string;
    taskId?: string;
  }): Promise<AccessReconcileJournalEntry[]>;
};

export const accessApiRef = createApiRef<AccessApi>({
  id: 'plugin.access.service',
});

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with status ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export class AccessApiClient implements AccessApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('access');
  }

  async getRoles(): Promise<AccessRoleResource[]> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/roles`,
    );
    const payload = await parseResponse<AccessRolesListResponse>(response);
    return payload.items;
  }

  async getAuditLog(
    options: {
      limit?: number;
      offset?: number;
      sortBy?: 'createdAt' | 'actor';
      order?: 'asc' | 'desc';
      resourceKind?: AccessAuditResourceKind;
      action?: AccessAuditAction;
      search?: string;
    } = {},
  ): Promise<{
    items: AccessAuditEntry[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const query = new URLSearchParams();
    if (options.limit) {
      query.set('limit', String(options.limit));
    }
    if (options.offset) {
      query.set('offset', String(options.offset));
    }
    if (options.sortBy) {
      query.set('sortBy', options.sortBy);
    }
    if (options.order) {
      query.set('order', options.order);
    }
    if (options.resourceKind) {
      query.set('resourceKind', options.resourceKind);
    }
    if (options.action) {
      query.set('action', options.action);
    }
    if (options.search?.trim()) {
      query.set('search', options.search.trim());
    }
    const queryString = query.toString();

    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/audit-log${
        queryString ? `?${queryString}` : ''
      }`,
    );
    return await parseResponse<AccessAuditLogListResponse>(response);
  }

  async getRole(id: string): Promise<AccessRoleResource> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/roles/${encodeURIComponent(id)}`,
    );
    return await parseResponse<AccessRoleResource>(response);
  }

  async createRole(role: AccessRoleInput): Promise<AccessRoleResource> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/roles`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(role),
      },
    );

    return await parseResponse<AccessRoleResource>(response);
  }

  async updateRole(
    id: string,
    role: AccessRoleInput,
  ): Promise<AccessRoleResource> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/roles/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(role),
      },
    );

    return await parseResponse<AccessRoleResource>(response);
  }

  async deleteRole(id: string): Promise<void> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/roles/${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
      },
    );

    await parseResponse<void>(response);
  }

  async getBindings(): Promise<AccessBindingResource[]> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/bindings`,
    );
    const payload = await parseResponse<AccessBindingsListResponse>(response);
    return payload.items;
  }

  async getBinding(id: string): Promise<AccessBindingResource> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/bindings/${encodeURIComponent(id)}`,
    );
    return await parseResponse<AccessBindingResource>(response);
  }

  async createBinding(
    binding: AccessBindingInput,
  ): Promise<AccessBindingResource> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/bindings`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(binding),
      },
    );

    return await parseResponse<AccessBindingResource>(response);
  }

  async updateBinding(
    id: string,
    binding: AccessBindingInput,
  ): Promise<AccessBindingResource> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/bindings/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(binding),
      },
    );

    return await parseResponse<AccessBindingResource>(response);
  }

  async deleteBinding(id: string): Promise<void> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/bindings/${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
      },
    );

    await parseResponse<void>(response);
  }

  async reconcile(
    request: AccessReconcileRequest,
  ): Promise<AccessReconcileResponse> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/reconcile`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );

    return await parseResponse<AccessReconcileResponse>(response);
  }

  async canCleanReconcileTables(): Promise<boolean> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/reconcile/clean-tables/allowed`,
    );
    const payload =
      await parseResponse<AccessReconcileCleanTablesAllowedResponse>(response);
    return payload.allowed;
  }

  async cleanReconcileTables(): Promise<AccessReconcileCleanTablesResponse> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/reconcile/clean-tables`,
      { method: 'POST' },
    );
    return await parseResponse<AccessReconcileCleanTablesResponse>(response);
  }

  async getReconcileRuns(
    options: {
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<AccessReconcileRunsListResponse> {
    const query = new URLSearchParams();
    query.set('limit', String(options.limit ?? 20));
    query.set('offset', String(options.offset ?? 0));

    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/reconcile/runs?${query.toString()}`,
    );
    return await parseResponse<AccessReconcileRunsListResponse>(response);
  }

  async getReconcileRunDetails(
    runId: string,
  ): Promise<AccessReconcileRunDetails> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/reconcile/runs/${encodeURIComponent(
        runId,
      )}/details`,
    );

    return await parseResponse<AccessReconcileRunDetails>(response);
  }

  async getReconcileStates(
    options: { limit?: number; phase?: string; runId?: string } = {},
  ): Promise<AccessReconcileResourceState[]> {
    const query = new URLSearchParams();
    if (options.limit) {
      query.set('limit', String(options.limit));
    }
    if (options.phase) {
      query.set('phase', options.phase);
    }
    if (options.runId) {
      query.set('runId', options.runId);
    }
    const queryString = query.toString();

    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/reconcile/states${
        queryString ? `?${queryString}` : ''
      }`,
    );
    const payload =
      await parseResponse<AccessReconcileResourceStatesListResponse>(response);
    return payload.items;
  }

  async getReconcileMaterializations(
    options: {
      limit?: number;
      ownerKind?: string;
      ownerRef?: string;
      taskId?: string;
      status?: string;
    } = {},
  ): Promise<AccessReconcileMaterialization[]> {
    const query = new URLSearchParams();
    if (options.limit) {
      query.set('limit', String(options.limit));
    }
    if (options.ownerKind) {
      query.set('ownerKind', options.ownerKind);
    }
    if (options.ownerRef) {
      query.set('ownerRef', options.ownerRef);
    }
    if (options.taskId) {
      query.set('taskId', options.taskId);
    }
    if (options.status) {
      query.set('status', options.status);
    }
    const queryString = query.toString();

    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/reconcile/materializations${
        queryString ? `?${queryString}` : ''
      }`,
    );
    const payload =
      await parseResponse<AccessReconcileMaterializationsListResponse>(
        response,
      );
    return payload.items;
  }

  async getReconcileJournal(
    options: {
      limit?: number;
      runId?: string;
      ownerRef?: string;
      taskId?: string;
    } = {},
  ): Promise<AccessReconcileJournalEntry[]> {
    const query = new URLSearchParams();
    if (options.limit) {
      query.set('limit', String(options.limit));
    }
    if (options.runId) {
      query.set('runId', options.runId);
    }
    if (options.ownerRef) {
      query.set('ownerRef', options.ownerRef);
    }
    if (options.taskId) {
      query.set('taskId', options.taskId);
    }
    const queryString = query.toString();

    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/reconcile/journal${
        queryString ? `?${queryString}` : ''
      }`,
    );
    const payload = await parseResponse<AccessReconcileJournalListResponse>(
      response,
    );
    return payload.items;
  }
}
